import React from 'react';
import { TableRow, TableCell, Typography, Link } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Helper function to convert numbers to ordinals (1st, 2nd, 3rd, etc.)
 */
const getOrdinal = (num) => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const mod100 = num % 100;
  return num + (suffixes[(mod100 - 20) % 10] || suffixes[mod100] || suffixes[0]);
};

/**
 * CourseRow component for displaying individual courses in the table
 * Supports drag and drop, selection states, and course information
 * @param {Object} course - Course data
 * @param {string} id - Unique course ID
 * @param {Set} selectedIds - Set of selected course IDs
 * @param {Set} backupIds - Set of backup course IDs
 * @param {Function} onToggleSelect - Function to handle course selection
 * @param {boolean} isDisabled - Whether the course is disabled
 * @param {Set} selectedCourses - Set of selected course codes (legacy, may be removed)
 * @param {Object} groupedCourses - All grouped courses for determining actual selections  
 */
const CourseRow = ({ course, id, selectedIds, backupIds, onToggleSelect, isDisabled, selectedCourses, groupedCourses }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const isSelected = selectedIds.has(id);
  const isBackup = backupIds.has(id);
  
  // Build a set of actually selected course codes (excluding backup)
  const actuallySelectedCourses = new Set();
  if (groupedCourses) {
    Object.values(groupedCourses).forEach(group => {
      group.courses.forEach(c => {
        const cId = c.is_split_course 
          ? `${c.semester_instance}_p${c.actual_period}` 
          : c.semester_instance;
        
        if (selectedIds.has(cId)) {
          actuallySelectedCourses.add(c.course_code);
        }
      });
    });
  }
  
  const isSelectedElsewhere = actuallySelectedCourses.has(course.course_code) && !isSelected && !isBackup;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDisabled ? 'not-allowed' : 'grab',
    backgroundColor: isDragging
      ? 'rgba(128, 128, 128, 0.2)'
      : isSelected
      ? 'rgba(25, 118, 210, 0.3)' // Blue highlight for selected
      : isBackup
      ? 'rgba(255, 193, 7, 0.3)' // Orange highlight for backup
      : isSelectedElsewhere
      ? 'rgba(255, 152, 0, 0.1)' // Light orange for selected elsewhere
      : 'inherit',
    opacity: isDisabled ? 0.5 : 1,
    color: isDisabled ? '#999' : 'inherit',
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (!isDisabled) {
      onToggleSelect(id);
    }
  };

  const handleLinkClick = (e) => {
    e.stopPropagation(); // Prevent row selection when clicking the link
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isDisabled ? {} : listeners)}
      onClick={handleClick}
    >
      <TableCell>
        {course.course_name}
        {course.is_split_course && (
          <Typography variant="caption" display="block" color="textSecondary">
            *{getOrdinal(course.period_index + 1)} part of {course.period_credits?.length || 0}{' '}
            (Total course: {course.original_credits} credits)
          </Typography>
        )}
        {isBackup && (
          <Typography variant="caption" display="block" color="warning.main">
            Backup choice
          </Typography>
        )}
        {isSelectedElsewhere && (
          <Typography variant="caption" display="block" color="warning.main">
            Already selected in another semester
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Link
          href={`https://www.uu.se/en/study/course?query=${course.course_code}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleLinkClick}
          color="primary"
          underline="hover"
        >
          {course.course_code}
        </Link>
      </TableCell>
      <TableCell>
        {course.credits}{course.is_split_course ? '*' : ''}
      </TableCell>
      <TableCell>{course.notes || ''}</TableCell>
    </TableRow>
  );
};

export default CourseRow; 